import {isNil} from 'ramda';
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {fsCockpitAuthApi} from 'lib/api/fsCockpitAuthApi';
import jwt_decode, {JwtPayload} from 'jwt-decode';
import {AppState} from "lib/redux/store";
import {FSCockpitJwtPayload, SPECIAL_SALES_ROLE} from "./FsCockpitAuth";

const LOCAL_STORAGE_AUTH_INFO_KEY = 'auth';

interface AuthTokens {
  accessToken: string | null
  refreshToken: string | null
}

interface AuthState extends AuthTokens {
  triedRefresh: boolean
  customer: string | null
  isSales: boolean
  requests: {
    login: {
      loading: boolean,
      error: string | null | undefined
    },
    refresh: {
      loading: boolean,
      error: string | null | undefined
    }
  }
}

interface Credentials {
  username: string
  password: string
}

const _initialLocalStorageInfo = getAuthInfoFromLocalStorage();
const _decodedToken = decodeToken(_initialLocalStorageInfo.accessToken);

const initialState: AuthState = {
  ...(_initialLocalStorageInfo),
  triedRefresh: false,
  customer: null,
  isSales: isSalesUser(_decodedToken),
  requests: {
    login: {
      loading: false,
      error: null
    },
    refresh: {
      loading: false,
      error: null
    }
  }
}

const selectors = {
  accessToken: (state: AppState) => state.auth.accessToken,
  refreshToken: (state: AppState) => state.auth.refreshToken,
  triedRefresh: (state: AppState) => state.auth.triedRefresh,
  requestPending: (state: AppState) => state.auth.requests.login.loading || state.auth.requests.refresh.loading,
  loggedIn: (state: AppState) => {
    const accessToken = selectors.accessToken(state);
    if (isNil(accessToken)) {
      return false;
    }
  
    try {
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const expInSeconds = jwt_decode<JwtPayload>(accessToken).exp ?? nowInSeconds + 600;
      return nowInSeconds < expInSeconds;
    } catch (e) {
      console.error('could not decode jwt token', e);
      return false;
    }
  },
  loginError: (state: AppState) => state.auth.requests.login.error,
  isSalesUser: (state: AppState) => state.auth.isSales,
  customer: (state: AppState) => state.auth.customer
};

const login = createAsyncThunk(
  'auth/login',
  (credentials: Credentials) => fsCockpitAuthApi.login(credentials.username, credentials.password)
);

const refresh = createAsyncThunk(
  'auth/refresh',
  (_: void, thunk) => {
    const state = thunk.getState() as AppState;
    const alreadyTriedRefresh = selectors.triedRefresh(state);
    if (alreadyTriedRefresh) {
      return Promise.reject();
    }

    const refreshToken = selectors.refreshToken(state);
    // can be null if logged in via customer app url params, where we don't get a refresh token
    if (isNil(refreshToken)) {
      return Promise.reject();
    }

    return fsCockpitAuthApi.refresh(refreshToken);
  }
);

const {actions, reducer} = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    tokenLogin: (state, action) => {
      onSuccessfulLogin(state, {
        access_token: action.payload.token,
        refresh_token: null
      });
    },
    setCustomer: (state, action: PayloadAction<string | null>) => {
      state.customer = action.payload
    },
    logout: (state) => {
      state.triedRefresh = false;
      onLogout(state);
    }
  },
  extraReducers: builder => {
    // TODO: Try builder.addCase(login.fulfilled, (state, action) => {
    builder.addCase(login.pending, (state: AuthState) => {
      state.requests.login.loading = true;
      state.requests.login.error = null;
      state.triedRefresh = false;
      state.accessToken = null;
      state.refreshToken = null;
    });

    builder.addCase(login.fulfilled, (state, action) => {
      state.requests.login.loading = false;
      state.requests.login.error = null;
      onSuccessfulLogin(state, action.payload);
    });

    builder.addCase(login.rejected, (state, action) => {
      state.requests.login.loading = false;
      state.requests.login.error = action.error.message;
      state.triedRefresh = false;
      onLogout(state);
    });


    builder.addCase(refresh.pending, state => {
      state.requests.refresh.loading = true;
      state.requests.refresh.error = null;
      state.accessToken = null;
    });

    builder.addCase(refresh.fulfilled, (state, action) => {
      state.requests.refresh.loading = false;
      state.requests.refresh.error = null;
      onSuccessfulLogin(state, action.payload);
    });

    builder.addCase(refresh.rejected, (state, action) => {
      state.requests.refresh.loading = false;
      state.requests.refresh.error = action.error.message;
      state.triedRefresh = true;
      onLogout(state);
    });
  }
});

interface LoginResponse {
  access_token: string
  refresh_token: string | null
}

function onSuccessfulLogin(state: AuthState, response: LoginResponse): void {
  setStateFromSuccessfulAuthResponse(state, response);
  saveAuthInfoToLocalStorage({
    accessToken: response.access_token,
    refreshToken: response.refresh_token
  });
}

function onLogout(state: AuthState): void {
  clearAuthInfoFromLocalStorage();
  setLoggedOutState(state);
}

function setStateFromSuccessfulAuthResponse(state: AuthState, response: LoginResponse) {
  state.triedRefresh = false;
  state.accessToken = response.access_token;
  state.refreshToken = response.refresh_token;

  const decodedToken = decodeToken(response.access_token);
  // state.customer = null;
  state.isSales = isSalesUser(decodedToken);
}

function decodeToken(accessToken: string | null): FSCockpitJwtPayload | null {
  return accessToken != null ? jwt_decode<FSCockpitJwtPayload>(accessToken) : null;
}

function isSalesUser(decodedToken: FSCockpitJwtPayload | null): boolean {
  const roles = decodedToken?.roles ?? [];
  return roles.includes(SPECIAL_SALES_ROLE);
}

// function getCustomerNoFromAuthToken(decodedToken: FSCockpitJwtPayload | null): string | null {
//   // WARNING: decodedToken?.sub is the login name, and not the customer number
//   //          sometimes it is the email and sometimes the customer number
//   //          depending how you logged in
//   //
//   // IDEA: Can we set the customer number from the link and ignore the sub?
//   //       Should we store the customer number in the local storage?
//   return isSalesUser(decodedToken) ? null : (decodedToken?.sub ?? null);
// }

function getAuthInfoFromLocalStorage(): AuthTokens {
  const localStorageString = localStorage.getItem(LOCAL_STORAGE_AUTH_INFO_KEY);
  if (localStorageString == null) {
    return {accessToken: null, refreshToken: null};
  }

  return JSON.parse(localStorageString) as AuthTokens;
}

function saveAuthInfoToLocalStorage(authState: AuthTokens): void {
  localStorage.setItem(LOCAL_STORAGE_AUTH_INFO_KEY, JSON.stringify(authState));
}

function clearAuthInfoFromLocalStorage(): void {
  localStorage.removeItem(LOCAL_STORAGE_AUTH_INFO_KEY);
}

function setLoggedOutState(state: AuthState): void {
  state.accessToken = null;
  state.refreshToken = null;
  state.triedRefresh = false;
}


const publicActions = {
  login,
  setCustomer: actions.setCustomer,
  tokenLogin: actions.tokenLogin,
  logout: actions.logout,
  refresh
};

export {publicActions as authActions};

const publicSelectors = {
  loggedIn: selectors.loggedIn,
  customer: selectors.customer,
  isSalesUser: selectors.isSalesUser,
  requestPending: selectors.requestPending,
  accessToken: selectors.accessToken,
  loginError: selectors.loginError
};

export {publicSelectors as authSelectors};

export {reducer as authReducer};